/**
 * 全系統時區與時間格式化工具
 *
 * 設計原則：
 * - 後端存儲 UTC 時間（datetime 格式）
 * - 前端根據用戶時區顯示
 * - 系統默認時區：Asia/Taipei（台灣時間 UTC+8）
 * - 用戶可在個人設置中更改顯示時區
 */

// 系統時區設定（後續可擴展為用戶可配置）
const SYSTEM_TIMEZONE = 'Asia/Taipei'

/**
 * 將 UTC 時間字串轉換為指定時區的格式化時間
 * @param utcDatetime UTC 時間字串 (格式: "2026-06-29 03:56" 或 "2026-06-29 03:56:00" 或 ISO 8601)
 * @param timezone 時區 (預設為系統時區)
 * @param format 格式 ('YYYY-MM-DD HH:MM' | 'YYYY-MM-DD HH:MM:SS' | 'full')
 * @returns 格式化後的時間字串
 */
export function formatDateTimeInTimezone(
  utcDatetime: string,
  timezone: string = SYSTEM_TIMEZONE,
  format: 'YYYY-MM-DD HH:MM' | 'YYYY-MM-DD HH:MM:SS' | 'full' = 'YYYY-MM-DD HH:MM'
): string {
  try {
    // 解析 UTC 時間
    // SQLite 返回格式: "YYYY-MM-DD HH:MM:SS"，需轉換為 ISO 8601 格式並加 Z 後綴表示 UTC
    let date: Date

    if (utcDatetime.includes('T')) {
      // ISO 8601 格式
      date = new Date(utcDatetime)
    } else {
      // SQLite 格式："YYYY-MM-DD HH:MM:SS" 或 "YYYY-MM-DD HH:MM"
      // 轉換為 ISO 8601 格式: "YYYY-MM-DDTHH:MM:SSZ"
      const isoFormat = utcDatetime.replace(' ', 'T') + 'Z'
      date = new Date(isoFormat)
    }

    // 轉換為指定時區
    const formatter = new Intl.DateTimeFormat('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: timezone,
      hour12: false,
    })

    const parts = formatter.formatToParts(date)
    const dateMap = {
      year: '',
      month: '',
      day: '',
      hour: '',
      minute: '',
      second: '',
    }

    parts.forEach((part) => {
      if (part.type in dateMap) {
        dateMap[part.type as keyof typeof dateMap] = part.value
      }
    })

    const { year, month, day, hour, minute, second } = dateMap

    switch (format) {
      case 'YYYY-MM-DD HH:MM':
        return `${year}-${month}-${day} ${hour}:${minute}`
      case 'YYYY-MM-DD HH:MM:SS':
        return `${year}-${month}-${day} ${hour}:${minute}:${second}`
      case 'full':
        return `${year}-${month}-${day} ${hour}:${minute}:${second} ${timezone}`
      default:
        return `${year}-${month}-${day} ${hour}:${minute}`
    }
  } catch (error) {
    console.error('Error formatting datetime:', error)
    return utcDatetime
  }
}

/**
 * 取得系統預設時區
 */
export function getSystemTimezone(): string {
  return SYSTEM_TIMEZONE
}

/**
 * 取得用戶時區（後續可從 userStore 讀取）
 */
export function getUserTimezone(): string {
  // TODO: 整合用戶設置，目前使用系統默認時區
  return SYSTEM_TIMEZONE
}

/**
 * 取得時區偏移量（小時）
 * @param timezone 時區
 * @returns 偏移量小時數 (如 +8 表示 UTC+8)
 */
export function getTimezoneOffset(timezone: string = SYSTEM_TIMEZONE): number {
  try {
    const now = new Date()
    const utcString = now.toLocaleString('en-US', { timeZone: 'UTC' })
    const tzString = now.toLocaleString('en-US', { timeZone: timezone })

    const utcDate = new Date(utcString)
    const tzDate = new Date(tzString)

    const offsetMs = tzDate.getTime() - utcDate.getTime()
    return offsetMs / (1000 * 60 * 60)
  } catch (error) {
    console.error('Error getting timezone offset:', error)
    return 8 // 預設台灣時區
  }
}

/**
 * 取得時區显示文字 (如 "UTC+8")
 */
export function getTimezoneDisplay(timezone: string = SYSTEM_TIMEZONE): string {
  const offset = getTimezoneOffset(timezone)
  const sign = offset >= 0 ? '+' : ''
  return `UTC${sign}${offset}`
}
